/**
 * NotificationPopup — the full text of one inbox entry, opened from the header bell: the source
 * (kind) as a tag, the title, the time, the whole body as plain text (no markdown rendering —
 * comment text is employee input), and an "Open" button when the entry points at a page.
 * z-50 like the company-event popup; DialogProvider alerts sit above (z-60).
 */
import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { NotificationRecord } from '@system/shared'
import { useT } from '../i18n/language-provider'
import { Button } from './ui/button'
import { Icon } from './ui/icon'
import { KIND_ICON, KIND_TONE, kindLabelKey } from './notification-kind'

interface Props {
  item: NotificationRecord
  onClose: () => void
}

/**
 * The body, lightly structured: the first non-empty line reads as a lead (what the notice is about),
 * lines that start with "- " or "•" render as bullets, everything else is a plain paragraph. The text
 * is still employee input, so nothing here interprets markdown beyond these two shapes.
 */
function NotifyBody({ text }: { text: string }) {
  const lines = text.split('\n')
  const firstIdx = lines.findIndex((l) => l.trim() !== '')
  return (
    <div
      id="notify-detail-modal-body"
      className="mx-5 mt-4 max-h-[60vh] space-y-0.5 overflow-y-auto border-t border-border pt-4 text-sm leading-relaxed"
    >
      {lines.map((line, i) => {
        const bullet = /^\s*[-•]\s+(.*)$/.exec(line)
        if (bullet) {
          return (
            <div key={i} className="flex gap-2 text-fg-soft">
              <span className="select-none text-primary">•</span>
              <span className="min-w-0 break-words">{bullet[1]}</span>
            </div>
          )
        }
        if (line.trim() === '') return <div key={i} className="h-2" />
        return (
          <p key={i} className={i === firstIdx ? 'break-words font-semibold text-fg' : 'break-words text-fg-soft'}>
            {line}
          </p>
        )
      })}
    </div>
  )
}

export function NotificationPopup({ item, onClose }: Props) {
  const t = useT()
  const navigate = useNavigate()
  // "/" is the sentinel for "nothing to open" (notifications.contract). Anything else is an SPA path
  // — e.g. a mention points at /issues/<project>/<key>, so the reader jumps straight to the issue.
  const hasLink = !!item.url && item.url !== '/'
  function openTarget() {
    if (!hasLink) return
    onClose()
    void navigate({ to: item.url })
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      id="notify-detail-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="notify-detail-modal-title"
        className="w-full max-w-md overflow-hidden rounded-xl border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 pb-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${KIND_TONE[item.kind]}`}
          >
            <Icon name={KIND_ICON[item.kind]} className="h-3.5 w-3.5" />
            {t(kindLabelKey(item.kind))}
          </span>
          <button
            id="notify-detail-modal-close-btn"
            type="button"
            onClick={onClose}
            aria-label={t('notify.close')}
            className="-mr-1 -mt-1 rounded-full p-1.5 text-muted hover:bg-hover hover:text-fg-soft"
          >
            <Icon name="close" className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5">
          <h2 id="notify-detail-modal-title" className="break-words text-xl font-bold leading-snug text-fg">
            {item.title}
          </h2>
        </div>

        {item.body && <NotifyBody text={item.body} />}

        <div className="mt-5 flex justify-end gap-2 border-t border-border bg-app/50 px-5 py-3">
          <Button id="notify-detail-modal-ok-btn" variant="outline" onClick={onClose}>
            {t('notify.close')}
          </Button>
          {hasLink && (
            <Button id="notify-detail-modal-open-btn" onClick={openTarget}>
              <Icon name="external" className="mr-1 inline h-4 w-4" />
              {t(item.kind === 'issue' ? 'notify.viewIssue' : 'notify.open')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
